import type {WandbWorkspacePanelPart} from '@wandb/forge-shell-contract/aria';
import {useRef, useState} from 'react';
import {toast} from 'react-toastify';

import {panelViews} from '../../components/PanelBank/types';
import type {ComposerImage} from '../chat/composer';
import {useWBAgentController} from '../chat/controller';
import {entryPointPlaceholders} from '../components/suggestions/catalog';
import {
  PanelScreenshotError,
  renderPanelImage,
} from '../components/tools/wandbPanel/capturePanelImage';
import {
  useTrackWBAgentComposer,
  useTrackWBAgentPanelScreenshot,
} from '../components/useTrackWBAgent';

const SCREENSHOT_PANEL_KINDS: Record<string, string> = {
  [panelViews.runHistoryLinePlot]: 'line',
  [panelViews.barChart]: 'bar',
  [panelViews.scatterPlot]: 'scatter',
  [panelViews.parallelCoordinatesPlot]: 'parallel_coordinates',
};

export function useAskAriaPanel() {
  const {startChat} = useWBAgentController();
  const {trackEntryPointClicked, trackMentionAdded} = useTrackWBAgentComposer();
  const track = useTrackWBAgentPanelScreenshot();
  const pending = useRef(false);
  const [isCapturing, setIsCapturing] = useState(false);

  const askAriaPanel = async (
    attachment: WandbWorkspacePanelPart,
    canCapture: boolean,
    panelDOMID: string
  ) => {
    if (pending.current) {
      return;
    }
    pending.current = true;
    setIsCapturing(true);
    const kind =
      SCREENSHOT_PANEL_KINDS[attachment.panelType] ??
      attachment.panelType
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_|_$/g, '');
    const attach = (images: ComposerImage[] = []) =>
      startChat([], {
        attachments: [attachment, ...images],
        delivery: 'draft',
        placeholder: entryPointPlaceholders.panel,
        target: 'active-or-new',
      });
    try {
      trackEntryPointClicked('panel_overflow_menu');
      const reportFailure = (cause: unknown) => {
        track({
          action: 'panel_screenshot_failed',
          panel_kind: kind,
          screenshot_source: 'workspace_panel',
          screenshot_failure:
            cause instanceof PanelScreenshotError
              ? cause.code
              : 'capture-failed',
        });
        toast.error(
          cause instanceof PanelScreenshotError
            ? cause.message
            : 'Could not capture this panel. Try again.'
        );
      };
      const panel = document.getElementById(panelDOMID);
      let captured: Awaited<ReturnType<typeof renderPanelImage>>;
      if (canCapture && panel) {
        track({
          action: 'panel_screenshot_attempted',
          panel_kind: kind,
          screenshot_source: 'workspace_panel',
        });
        try {
          captured = await renderPanelImage({
            panel,
            kind,
            source: 'workspace_panel',
          });
        } catch (cause) {
          reportFailure(cause);
        }
      }
      let outcome = attach(captured ? [captured.image] : []);
      const screenshotAttached = !!captured && outcome.ok;
      if (!outcome.ok && outcome.reason === 'attachment-limit') {
        reportFailure(
          new PanelScreenshotError(
            'over-limit',
            'The panel screenshot exceeds the chat attachment limits. Remove an image and try again.'
          )
        );
        outcome = attach();
      }
      if (outcome.ok) {
        trackMentionAdded('panel_overflow_menu', 'panel');
        if (screenshotAttached && captured) {
          track({
            action: 'panel_screenshot_succeeded',
            panel_kind: kind,
            screenshot_source: 'workspace_panel',
            screenshot_bytes: captured.bytes,
          });
        }
      }
    } catch (error) {
      // Keep cleanup out of finally so React Compiler can memoize this hook.
      pending.current = false;
      setIsCapturing(false);
      throw error;
    }
    pending.current = false;
    setIsCapturing(false);
  };

  return {askAriaPanel, isCapturing};
}
